import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthSession } from '../login/auth-session.model';

export interface ExternalAuthProviderConfig {
  id: string;
  label: string;
}

export type ExternalAuthStatus = 'success' | 'error';

export interface ExternalAuthCallbackSuccess {
  status: 'success';
  token: string;
  provider?: string;
  rawParams: Record<string, string | null>;
}

export interface ExternalAuthCallbackError {
  status: 'error';
  code?: string;
  message?: string;
  provider?: string;
  rawParams: Record<string, string | null>;
}

export type ExternalAuthCallbackResult =
  | ExternalAuthCallbackSuccess
  | ExternalAuthCallbackError;

@Injectable({
  providedIn: 'root'
})
export class ExternalAuthService {
  private readonly apiBase = environment.apiBase;
  private readonly config = environment.externalAuth;

  getEnabledProviders(): ExternalAuthProviderConfig[] {
    if (!this.config || !this.config.providers) {
      return [];
    }

    return Object.entries(this.config.providers)
      .filter(([, providerConfig]) => providerConfig && providerConfig.enabled)
      .map(([id, providerConfig]) => ({
        id,
        label: providerConfig.label || this.toTitleCase(id)
      }));
  }

  getSpaCallbackUrl(): string {
    const origin =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : '';
    const path = this.getSpaCallbackPath();

    if (!origin) {
      return path;
    }

    return this.joinUrl(origin, path);
  }

  buildChallengeUrl(providerId: string, returnUrl?: string | null): string {
    if (!providerId) {
      throw new Error('Provider is required to build challenge URL.');
    }

    const base = this.joinUrl(this.apiBase, this.config.challengePath);
    const query = new URLSearchParams();
    query.set('provider', providerId);

    if (returnUrl) {
      query.set('returnUrl', returnUrl);
    }

    return `${base}?${query.toString()}`;
  }

  beginExternalLogin(providerId: string, returnUrl?: string | null): void {
    const challengeUrl = this.buildChallengeUrl(providerId, returnUrl);
    window.location.href = challengeUrl;
  }

  parseCallback(paramMap: ParamMap): ExternalAuthCallbackResult {
    const allParams = this.getAllParams(paramMap);
    const status = (paramMap.get('status') || '').toLowerCase() as ExternalAuthStatus;

    if (status === 'success') {
      return {
        status,
        token: paramMap.get('token') || '',
        provider: paramMap.get('provider') || undefined,
        rawParams: allParams
      };
    }

    return {
      status: 'error',
      code: paramMap.get('code') || undefined,
      message: paramMap.get('message') || undefined,
      provider: paramMap.get('provider') || undefined,
      rawParams: allParams
    };
  }

  buildSessionFromCallback(
    callback: ExternalAuthCallbackSuccess
  ): AuthSession | null {
    if (!callback.token) {
      return null;
    }

    const payload = this.decodeJwtPayload(callback.token);
    const roleIdRaw = callback.rawParams['roleId'];
    const clientId = callback.rawParams['clientId'];
    const clientNameRaw = callback.rawParams['clientName'];
    const clientName =
      clientNameRaw && clientNameRaw.trim().length > 0
        ? clientNameRaw
        : 'CompPBM';
    const firstName =
      callback.rawParams['firstName'] || this.extractFirstNameFromPayload(payload) || 'User';
    const userId =
      callback.rawParams['userId'] || this.extractUserIdFromPayload(payload);
    const pwdVerifiedRaw =
      callback.rawParams['isPwdVerified'] != null
        ? callback.rawParams['isPwdVerified']
        : 'true';
    const email =
      callback.rawParams['email'] || this.extractEmailFromPayload(payload);
    const roleId = roleIdRaw || '3';

    if (!userId) {
      return null;
    }

    return {
      token: callback.token,
      roleId,
      clientId: this.normalizeClientId(clientId),
      clientName,
      firstName,
      userId,
      isPwdVerified: this.parseBoolean(pwdVerifiedRaw),
      email: email || undefined,
      provider: callback.provider
    };
  }

  getCallbackPath(): string {
    return this.config && this.config.callbackPath
      ? this.config.callbackPath
      : 'api/ExternalAuth/ExternalLoginCallback';
  }

  private extractEmailFromToken(token: string): string | null {
    try {
      const payload = this.decodeJwtPayload(token);
      return this.extractEmailFromPayload(payload);
    } catch {
      return null;
    }
  }

  private decodeJwtPayload(token: string): any {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const payload = parts[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = this.padBase64(normalized);
      const decoded = window.atob(padded);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private extractEmailFromPayload(payload: any): string | null {
    if (!payload) {
      return null;
    }

    return payload.sub || payload.email || null;
  }

  private extractFirstNameFromPayload(payload: any): string | null {
    if (!payload) {
      return null;
    }

    const userDataClaim =
      payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'];
    if (typeof userDataClaim === 'string' && userDataClaim.trim().length > 0) {
      return userDataClaim.trim().split(' ')[0];
    }

    if (typeof payload.given_name === 'string') {
      return payload.given_name;
    }

    return null;
  }

  private extractUserIdFromPayload(payload: any): string | null {
    if (!payload) {
      return null;
    }

    return (
      payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      payload['http://schemas.microsoft.com/identity/claims/objectidentifier'] ||
      payload.sub ||
      null
    );
  }

  private padBase64(value: string): string {
    const padding = value.length % 4;
    if (padding === 2) {
      return value + '==';
    }
    if (padding === 3) {
      return value + '=';
    }
    if (padding !== 0) {
      return value + '==';
    }
    return value;
  }

  private parseBoolean(value: string | null): boolean {
    return value === 'true' || value === '1';
  }

  private normalizeClientId(value: string | null): number | string | null {
    if (value == null) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  private joinUrl(base: string, path: string): string {
    if (!path) {
      return base;
    }

    if (base.endsWith('/') && path.startsWith('/')) {
      return base + path.substr(1);
    }

    if (!base.endsWith('/') && !path.startsWith('/')) {
      return `${base}/${path}`;
    }

    return base + path;
  }

  private getSpaCallbackPath(): string {
    const rawPath =
      this.config &&
      (this.config as { spaCallbackPath?: string }).spaCallbackPath;
    const normalized =
      typeof rawPath === 'string' ? rawPath.trim() : '';

    if (!normalized) {
      return '/auth/sso-callback';
    }

    return normalized.startsWith('/')
      ? normalized
      : `/${normalized}`;
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return value;
    }

    return value
      .replace(/([A-Z])/g, ' $1')
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, char => char.toUpperCase());
  }

  private getAllParams(paramMap: ParamMap): Record<string, string | null> {
    const params: Record<string, string | null> = {};
    paramMap.keys.forEach(key => {
      params[key] = paramMap.get(key);
    });
    return params;
  }
}
